import { Logger, LogLevel } from './app-error-logger';
import { Storage } from '@ionic/storage-angular';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { HashSet } from 'src/app/shared/helper/hashset';
import { Platform } from '@ionic/angular';
import { FileHelper } from './file-helper';
import { Utils } from './utils';
import { Constants } from '../model/constants';
import { MyApp } from './app';
import { AuftragEx } from '../model/model';
import CordovaSQLiteDriver from 'localforage-cordovasqlitedriver'
import { Filesystem, Directory } from '@capacitor/filesystem';

const log = new Logger('AppStorage');

export class AppStorage {
    static current: AppStorage = new AppStorage();

    private storageReady = false;

    private platform: Platform = null;
    private storage: Storage = null;
    private file: File = null;

    /**
     * Ist TRUE während die lokalen Daten gelöscht werden
     */
    datenWerdenGeloescht = false;

    dataCache = {};

    private constructor() {
    }

    init(storage: Storage, file: File, platform: Platform) {
        log?.info('INIT');

        this.storage = storage;
        this.file = file;
        this.platform = platform;

        MyApp.ready().then(async () => {
            try {
                // TODO CAPACITOR: Prüfen warum der Promise von storage.create() nicht resolved wird
                // this.storageReady = true;

                log?.info('Defining driver for SQLite');
                await this.storage.defineDriver(CordovaSQLiteDriver);

                log?.info('Create Storage...');
                this.storage = await this.storage.create();
                log?.info('Create Storage... Done');

                log?.info('STORAGE READY');
                this.storageReady = true;
            } catch (err) {
                log.error('Error during AppStorage initialization: ' + Utils.getErrorMessage(err));
            }
        });

        this.bereinigen();
    }

    async get(key: string, cache = true, useLocalStorage = false): Promise<any> {
        if (cache) {
            log?.debug('GET: ' + key);
        } else {
            log?.debug('GET: ' + key + '(NO CACHE)');
        }

        if (cache) {
            if (this.dataCache.hasOwnProperty(key)) {
                log?.debug('GET: ' + key + ' (CACHE HIT)');
                return this.dataCache[key];
            }

            if (useLocalStorage) {
                // Prüfen ob die Daten im localStorage vorhanden sind.
                // Ist viel schneller als SQLigth über "storage"
                const json = localStorage.getItem(key);

                if (json) {
                    log?.debug('GET: ' + key + ' from localStorage');

                    const dataFromLocalStorage = JSON.parse(json);
                    this.dataCache[key] = dataFromLocalStorage;
                    return dataFromLocalStorage;
                }
            }
        }

        if (!this.storageReady) {
            await this.waitForStorageReady();
        }

        const data = await this.storage.get(key);

        if (cache && data) {
            this.dataCache[key] = data;
        }

        return data;
    }

    async waitForStorageReady() {
        let timeout = Date.now() + 30_000; // 30 Sekunden Timeout

        while (!this.storageReady) {
            log?.debug('Waiting for storage ready...');
            await Utils.delay(250);

            if (Date.now() > timeout) {
                throw new Error('Timeout waiting for storage ready');
            }
        }
    }

    async set(key: string, data: any, cache = true, useLocalStorage = false): Promise<void> {
        if (cache) {
            log?.debug('SET: ' + key + ' (cache, useLocalStorage=' + useLocalStorage + ')', data);
        } else {
            log?.debug('SET: ' + key + ' (useLocalStorage=' + useLocalStorage + ')', data);
        }

        // // debugg
        // if (key.startsWith("auftrag-")) {
        //     const auftrag = data as AuftragEx;

        //     if (auftrag.Positionen?.length) {
        //         log?.debug('SET: ' + key + ' Behälter:' + auftrag.Positionen[0].Behaelter[0]?.BehaelterNr);
        //     }
        // }

        if (cache) {
            this.dataCache[key] = data;

            if (useLocalStorage) {
                // Auch im localStorage speichern
                try {
                    localStorage.setItem(key, JSON.stringify(data));
                } catch (err) {
                    log.warn(`Fehler beim Speichern im localStorage (${key}): ${Utils.getErrorMessage(err)}`, data);
                    localStorage.removeItem(key);
                }
            } else {
                localStorage.removeItem(key);
            }
        } else {
            // Eintrag soll nicht im Cache gespeichert werden. Ein eventueller Eintrag im Cache muss aber gelöscht werden
            // Das tritt z.B. beim Aktualisieren von Aufträgen auf. Wenn der Auftrag schon angezeigt wurde, befindet er sich im Cache.
            // Wenn er dann aktualisiert wird, muss der Cache geleert werden
            delete this.dataCache[key];

            localStorage.removeItem(key);
        }

        if (!this.storageReady) {
            await this.waitForStorageReady();
        }

        log?.debug('storage set: ' + key);

        await this.storage.set(key, data);

        log?.debug('storage set: ' + key + ' done');
    }

    async clear() {
        log?.debug('CLEAR');

        // TODO: local storage löschen

        this.dataCache = {};

        if (!this.storageReady) {
            await this.waitForStorageReady();
        }

        await this.storage.clear();
    }

    async remove(key: string) {
        log?.debug('REMOVE: ' + key);

        localStorage.removeItem(key);

        delete this.dataCache[key];

        if (!this.storageReady) {
            await this.waitForStorageReady();
        }

        await this.storage.remove(key);
    }

    async getBildBase64Data(guid: string): Promise<string> {
        const src = await this.getBildSrc(guid);

        if (!src) {
            return null;
        }

        let bildBase64 = '';

        if (src && src.startsWith('file:')) {
            // Bild wurde als Pfad abgespeichert. Datei jetzt einlesen
            // Beispiel: file:///data/user/0/de.recomobil.app/files/auftrag-2011-1570483448148.jpg

            const filename = FileHelper.getFilename(src);
            const directory = FileHelper.getDirectory(src);

            bildBase64 = await this.file.readAsDataURL(directory, filename);
        } else {
            bildBase64 = src;
        }

        if (bildBase64.startsWith("data:image/")) {
            // An den Server nur die Base64-Daten schicken
            const pos = bildBase64.indexOf('base64,');

            if (pos) {
                bildBase64 = bildBase64.substring(pos + 7);
            }
        }

        return bildBase64;
    }

    async loescheBild(guid: string): Promise<void> {
        if (!guid) {
            return;
        }

        try {
            const src = await this.getBildSrc(guid);

            if (src && src.startsWith('file:')) {
                const filename = FileHelper.getFilename(src);
                const directory = FileHelper.getDirectory(src);

                try {
                    await this.file.removeFile(directory, filename);
                } catch (err) {
                    log.warn('error removing file', err);
                }
            }

            await AppStorage.current.remove('bild-' + guid);
        } catch (err) {
            log.error('loescheBild ' + guid + ': ' + Utils.getErrorMessage(err));
        }
    }

    async getBildSrc(guid: string): Promise<string> {
        if (!guid) {
            log.warn('getBildSrc guid is null');
            return null;
        }

        const src = await AppStorage.current.get('bild-' + guid, false) as string;

        if (!src) {
            log.warn('getBildSrc src is null, guid=' + guid);
            return null;
        }

        return src;
    }

    /**
     * Wird initial aufgerufen
     */
    async bereinigen() {
        log?.info('bereinigen');

        for (const key in localStorage) {
            if (key) {
                if (key.startsWith('auftrag-')
                    || key == 'aktuelleTour'
                    || key == Constants.AUFTRAEGE
                    || key == 'uvv-gegenstaende'
                    || key == 'nachrichten') {

                    log?.info('bereinigen: Remove from localStorage: ' + key);
                    localStorage.removeItem(key);
                }
            }
        }
    }

    /**
     * Wird nur beim expliziten Löschen der lokalen Daten aufgerufen
     */
    async loescheAlleDaten() {
        log.warn('LÖSCHE ALLE DATEN');

        this.datenWerdenGeloescht = true;

        try {

            for (const key in localStorage) {
                if (key) {
                    if (key.startsWith('auftrag-') || key == 'aktuelleTour' || key == Constants.AUFTRAEGE || key == 'uvv-gegenstaende' || key == 'nachrichten') {
                        log?.info('DELETE LOCAL STORAGE: ' + key);
                        localStorage.removeItem(key);
                    }
                }
            }

            const keys = await this.storage.keys();

            for (const key of keys) {
                if (key.startsWith('auftrag-') || key.startsWith('bild-') || key == Constants.AUFTRAEGE || key == 'uvv-gegenstaende' || key == 'nachrichten') {
                    log?.info('DELETE STORAGE: ' + key);
                    this.storage.remove(key);
                }
            }
        } catch (err) {
            log.error(err);
        }

        this.datenWerdenGeloescht = false;
        this.dataCache = {};
    }
}
